// components/ConsentModal.tsx
import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { COLORS } from '../constants/colors';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
`;

const Modal = styled.div`
  background: white;
  border-radius: 16px;
  padding: 40px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Title = styled.h2`
  font-size: 1.75rem;
  margin-bottom: 12px;
  color: #333;
  text-align: center;
  font-weight: 600;
`;

const Message = styled.p`
  font-size: 1rem;
  color: #666;
  text-align: center;
  line-height: 1.6;
  margin-bottom: 32px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 14px 24px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  ${props => props.variant === 'primary' ? `
    background: ${COLORS.PRIMARY};
    color: white;

    &:hover:not(:disabled) {
      background: ${COLORS.PRIMARY_DARK};
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
    }
  ` : `
    background: #f5f5f5;
    color: #666;

    &:hover:not(:disabled) {
      background: #e0e0e0;
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const Logo = styled.img`
  width: 80px;
  height: 80px;
  object-fit: contain;
  margin: 0 auto 20px;
  display: block;
`;

const ErrorMessage = styled.div`
  color: #d93025;
  background: #ffeaea;
  padding: 12px;
  border-radius: 6px;
  margin-top: 16px;
  text-align: center;
  font-size: 0.9rem;
`;

const ConsentModal: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { updateUser, user, logout } = useAuth();
  const { t } = useTranslation();

  const handleConsent = async (agreed: boolean) => {
    if (!agreed) {
      // User declined - log them out
      logout();
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/consent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        credentials: 'include',
        body: JSON.stringify({ consent: true })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Update user in context
        if (user) {
          updateUser({
            ...user,
            ageConsent: true,
          });
        }
        // Modal will automatically close when user.consent becomes true
      } else {
        setError(data.error || 'Failed to save consent. Please try again.');
      }
    } catch (err) {
      console.error('Consent error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay>
      <Modal>
        <Logo src="/assets/images/logo_writing.jpg" alt="Bedtime Stories Logo" />
        <Title>{t('consentModal.title')}</Title>
        <Message>
          {t('consentModal.message')}
        </Message>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <ButtonGroup>
          <Button 
            variant="secondary" 
            onClick={() => handleConsent(false)}
            disabled={loading}
          >
            {t('consentModal.underAge')}
          </Button>
          <Button 
            variant="primary" 
            onClick={() => handleConsent(true)}
            disabled={loading}
          >
            {loading ? t('consentModal.saving') : t('consentModal.confirm')}
          </Button>
        </ButtonGroup>
      </Modal>
    </Overlay>
  );
};

export default ConsentModal;